import { ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { Subject, firstValueFrom } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import type { Message, User } from 'devextreme/ui/chat';
import { emojiCatalog, emojiCategories, normalizeEmoji } from './emoji-catalog';
import { BadisOauthLoginService } from '@app/core/services/auth/badis-oauth-login.service';
import { SettingsService } from '@app/core/services/settings/settings.service';
import { DataService } from '@app/core/services/data.service';
import { ImageService } from '@app/shared/services/image.service';
import { ChatApiService } from '@app/core/services/chat/chat-api.service';
import { ChatHubConnectionService } from '@app/core/services/hubs/chat-hub-connection.service';
import { ChatStateService } from '@app/core/services/chat/chat-state.service';
import { ToastNotifyService } from '@app/pages/notifications/services/toast-notify.service';
import { AlertType } from '@app/shared/toasts/security-alert/models/security-alert';
import type { dxFileUploaderOptions } from 'devextreme/ui/file_uploader';
import {
    ChatConversationDto,
    ChatConversationType,
    ChatMessageDto,
    ChatMessageType,
    ChatSendMessageRequest,
    SimpleUserDto
} from '@app/core/models/chat/chat.models';

type NawizardUserProfileCard = {
    kind: 'nawizard.card.user_profile';
    version: number;
    data: {
        personId: string;
        displayName?: string | null;
    };
};

type NawizardCard = NawizardUserProfileCard & Record<string, any>;
type ChatUiMessage = Message & { rawText: string; card?: NawizardCard | null };

@Component({
    selector: 'app-chat-widget',
    templateUrl: './chat-widget.component.html',
    styleUrls: ['./chat-widget.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ChatWidgetComponent implements OnInit, OnChanges, OnDestroy {
    @Input() conversationId?: string;
    @Output() closed = new EventEmitter<void>();

    currentUser: User = { id: 'guest', name: 'Guest' };
    aiUser: User = { id: 'ai-assistant', name: 'AI Assistant' };
    systemUser: User = { id: 'system', name: 'System' };

    messages: ChatUiMessage[] = [];
    loading = true;
    messagesLoading = false;
    connectionReady = false;
    showEmojiPicker = false;
    emojiQuery = '';
    activeEmojiCategory = 'all';
    recentEmojis: string[] = [];
    listQuery = '';
    conversations: ChatConversationDto[] = [];
    users: SimpleUserDto[] = [];
    listLoading = false;
    showUserPicker = false;
    userQuery = '';
    private userQueryTimer?: number;

    // New properties for custom chat UI
    messageInput: string = '';
    isAiTyping: boolean = false;
    isRemoteTyping = false;

    readonly initialAiSuggestions: string[] = [
        'Merhaba! Size nasıl yardımcı olabilirim?',
        'Son raporları göster',
        'Sistem durumu nedir?'
    ];
    aiSuggestions: string[] = [];
    
    // Make enum accessible in template
    ChatConversationType = ChatConversationType;
    
    @ViewChild('messagesContainer') messagesContainer!: ElementRef;
    @ViewChild('messageInputEl') messageInputEl!: ElementRef;

    // Emoji kataloğu ayrı dosyadan import ediliyor (emoji-catalog.ts)
    readonly fallbackAvatarUrl = 'assets/images/logo/navizard.png';
    // AI Asistan sabit avatar - sihirli mor/gradient arka plan
    readonly aiAvatarUrl = null; // null = CSS gradient avatar kullan

    activeConversation: ChatConversationDto | null = null;
    activeConversationTitle = 'Sohbet';
    private pendingConversationId: string | null = null;
    private initialized = false;
    private messageIds = new Set<string>();
    private destroy$ = new Subject<void>();
    openConversationMenuId: string | null = null;
    private conversationRetryAttempts = 0;
    private conversationRetryTimer?: number;
    private typingTimeout?: number;
    private isTyping = false;
    private typingUsers = new Set<string>();
    private typingTimers = new Map<string, number>();

    fileUploaderOptions: Omit<dxFileUploaderOptions, 'dialogTrigger' | 'showFileList' | 'uploadMode' | 'value'> = {
        visible: false
    };

    constructor(
        private authService: BadisOauthLoginService,
        private settingsService: SettingsService,
        private dataService: DataService,
        private imageService: ImageService,
        private chatApi: ChatApiService,
        private chatHub: ChatHubConnectionService,
        private chatState: ChatStateService,
        private toastNotify: ToastNotifyService,
        private host: ElementRef<HTMLElement>,
        private cdr: ChangeDetectorRef
    ) {
        this.aiSuggestions = [...this.initialAiSuggestions];
    }

    async ngOnInit(): Promise<void> {
        this.setCurrentUserFromToken();
        this.chatHub.init();

        this.authService.onAuthStatusChanged
            .pipe(takeUntil(this.destroy$))
            .subscribe((isAuthenticated) => {
                if (isAuthenticated) {
                    this.setCurrentUserFromToken();
                    if (this.initialized) {
                        void this.handleAuthReady();
                    }
                } else {
                    this.currentUser = { id: 'guest', name: 'Guest' };
                }
            });

        this.chatHub.connectionReady
            .pipe(takeUntil(this.destroy$))
            .subscribe((ready) => {
                this.connectionReady = ready;
            });

        this.chatHub.newMessage$
            .pipe(takeUntil(this.destroy$))
            .subscribe((message) => this.handleIncomingMessage(message));

        this.chatHub.typingStarted$
            .pipe(takeUntil(this.destroy$))
            .subscribe((event) => this.handleTypingStarted(event));

        this.chatHub.typingStopped$
            .pipe(takeUntil(this.destroy$))
            .subscribe((event) => this.handleTypingStopped(event));

        this.loadRecentEmojis();
        await this.initializeChat();
        this.initialized = true;

        if (this.pendingConversationId && this.pendingConversationId !== this.activeConversation?.id) {
            await this.switchConversation(this.pendingConversationId);
            this.pendingConversationId = null;
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        const nextConversationId = changes['conversationId']?.currentValue as string | undefined;
        if (!nextConversationId || nextConversationId === this.activeConversation?.id) {
            return;
        }

        if (!this.initialized || this.loading) {
            this.pendingConversationId = nextConversationId;
            return;
        }

        void this.switchConversation(nextConversationId);
    }

    ngOnDestroy(): void {
        if (this.activeConversation?.id) {
            this.chatHub.leaveConversation(this.activeConversation.id);
        }
        this.stopTyping();

        this.destroy$.next();
        this.destroy$.complete();
    }

    close(): void {
        this.closed.emit();
    }

    private async initializeChat(): Promise<void> {
        this.loading = true;
        this.resolveSystemUsers();

        try {
            const loaded = await this.loadConversations();
            if (!loaded) {
                this.scheduleConversationRetry();
                return;
            }
            const initial = this.pickInitialConversation(this.conversations);
            const nextConversation = initial ?? await this.createAiConversation();
            this.setActiveConversationAsync(nextConversation);

            if (!nextConversation) {
                this.loading = false;
                return;
            }

            this.chatHub.joinConversation(nextConversation.id);
            void this.loadMessages(nextConversation.id);
            this.markConversationRead(nextConversation.id);
        } catch {
            // Intentionally silent for now; toast can be added later
            this.scheduleConversationRetry();
        } finally {
            setTimeout(() => {
                this.loading = false;
                this.cdr.markForCheck();
            }, 0);
        }
    }

    private async switchConversation(conversationId: string): Promise<void> {
        if (!conversationId || conversationId === this.activeConversation?.id) {
            return;
        }

        this.loading = true;
        this.clearTypingIndicators();
        try {
            let target = this.conversations.find((c) => c.id === conversationId);
            if (!target) {
                const conversations = await firstValueFrom(this.chatApi.getConversations());
                this.conversations = conversations ?? [];
                target = this.conversations.find((c) => c.id === conversationId);
            }
            if (!target) {
                return;
            }

            const otherId = this.getOtherMemberId(target);
            const canonical = otherId ? this.getCanonicalDirectConversation(otherId) : null;
            if (canonical) {
                target = canonical;
            }

            if (this.activeConversation?.id) {
                this.chatHub.leaveConversation(this.activeConversation.id);
            }

            this.setActiveConversationAsync(target);
            this.messages = this.getSystemMessagesForConversation(target.id);
            this.chatHub.joinConversation(target.id);
            this.stopTyping();
            void this.loadMessages(target.id);
            this.markConversationRead(target.id);
        } catch {
            // Intentionally silent for now; toast can be added later
        } finally {
            setTimeout(() => {
                this.loading = false;
                this.cdr.markForCheck();
            }, 0);
        }
    }

    openUserPicker(): void {
        this.openConversationMenuId = null;
        this.showUserPicker = true;
        this.userQuery = '';
        void this.loadUsers();
    }

    closeUserPicker(): void {
        this.showUserPicker = false;
        this.userQuery = '';
    }

    onUserQueryChanged(): void {
        if (this.userQueryTimer) {
            clearTimeout(this.userQueryTimer);
        }

        this.userQueryTimer = window.setTimeout(() => {
            void this.loadUsers();
        }, 250);
    }

    get filteredConversations(): ChatConversationDto[] {
        const query = this.listQuery.trim().toLowerCase();
        const sorted = [...this.conversations].sort((a, b) => {
            const left = new Date(a.lastMessageAt ?? 0).getTime();
            const right = new Date(b.lastMessageAt ?? 0).getTime();
            return right - left;
        });

        const deduped = this.dedupeDirectConversations(sorted);
        if (!query) {
            return deduped;
        }

        return deduped.filter((item) => this.getConversationTitle(item).toLowerCase().includes(query));
    }

    get filteredUsers(): SimpleUserDto[] {
        // API zaten query ile filtreleme yapıyor, client-side tekrar filtreleme yapmıyoruz
        // Böylece tüm API sonuçları gösterilir (AI Assistant da dahil)
        return this.users;
    }

    async selectConversation(conversation: ChatConversationDto): Promise<void> {
        if (!conversation?.id) {
            return;
        }

        this.openConversationMenuId = null;
        const otherId = this.getOtherMemberId(conversation);
        const target = otherId ? this.getCanonicalDirectConversation(otherId) : conversation;
        await this.switchConversation(target?.id ?? conversation.id);
    }

    toggleConversationMenu(conversation: ChatConversationDto, event?: MouseEvent): void {
        event?.stopPropagation();
        if (!conversation?.id) {
            return;
        }

        this.openConversationMenuId =
            this.openConversationMenuId === conversation.id ? null : conversation.id;
    }

    async deleteConversation(conversation: ChatConversationDto, event?: MouseEvent): Promise<void> {
        event?.stopPropagation();
        if (!conversation?.id) {
            return;
        }

        this.openConversationMenuId = null;
        const confirmed = confirm('Bu sohbeti silmek istediğinize emin misiniz?');
        if (!confirmed) {
            return;
        }

        const targetIds = new Set<string>();
        if (conversation.type === ChatConversationType.Direct) {
            const otherId = this.getOtherMemberId(conversation);
            if (otherId) {
                this.conversations
                    .filter((item) =>
                        item.type === ChatConversationType.Direct && (item.memberIds ?? []).includes(otherId)
                    )
                    .forEach((item) => targetIds.add(item.id));
            }
        }

        if (targetIds.size === 0) {
            targetIds.add(conversation.id);
        }

        try {
            for (const id of targetIds) {
                await firstValueFrom(this.chatApi.deleteConversation(id));
            }
        } catch {
            return;
        }

        this.conversations = this.conversations.filter((item) => !targetIds.has(item.id));

        if (this.activeConversation?.id && targetIds.has(this.activeConversation.id)) {
            this.chatHub.leaveConversation(this.activeConversation.id);
            this.setActiveConversationAsync(null);
            this.messages = [];
        }

        await this.loadConversations();

        if (!this.activeConversation) {
            const next = this.pickInitialConversation(this.conversations);
            if (next) {
                await this.switchConversation(next.id);
            }
        }

        this.chatState.refreshUnreadCount();
    }

    async startAiChat(user?: SimpleUserDto): Promise<void> {
        this.closeUserPicker();
        
        // Mevcut AI konuşmasını bul veya oluştur
        const existingAi = this.conversations.find(c => c.type === ChatConversationType.Ai);
        
        if (existingAi) {
            await this.switchConversation(existingAi.id);
            return;
        }
        
        // Veritabanından gelen AI kullanıcısı ile konuşma oluştur
        const aiUserId = user?.id || this.settingsService.getSettingValue<string>('Chat.AiAssistant.UserId');
        if (!aiUserId) return;
        
        try {
            const created = await firstValueFrom(this.chatApi.createConversation({
                type: ChatConversationType.Ai,
                title: user?.name || 'AI Assistant',
                memberIds: [aiUserId]
            }));
            
            if (created) {
                await this.loadConversations();
                await this.switchConversation(created.id);
            }
        } catch {
            // Hata durumunda sessizce geç
        }
    }

    async startDirectChat(user: SimpleUserDto): Promise<void> {
        if (!user?.id) {
            return;
        }

        this.closeUserPicker();

        const existing = this.getCanonicalDirectConversation(user.id);

        if (existing) {
            await this.switchConversation(existing.id);
            return;
        }

        try {
            const created = await firstValueFrom(this.chatApi.createConversation({
                type: ChatConversationType.Direct,
                title: user.name,
                memberIds: [user.id]
            }));

            if (created) {
                this.conversations = [created, ...this.conversations.filter((item) => item.id !== created.id)];
                await this.switchConversation(created.id);
            }
        } catch {
            // Intentionally silent for now; toast can be added later
        }
    }

    getUserAvatarUrl(user: SimpleUserDto): string {
        // AI kullanıcısı için özel kontrol - sihirli gradient avatar
        if (this.isAiUser(user?.id)) {
            return ''; // Boş döndür, template'de ai-avatar-icon gösterilecek
        }
        return this.resolveAvatarUrl(user?.id, user?.avatar, 96);
    }

    // Veritabanından gelen kullanıcı AI Assistant mı?
    isAiUser(userId: string | undefined): boolean {
        if (!userId) return false;
        const aiUserId = this.settingsService.getSettingValue<string>('Chat.AiAssistant.UserId');
        return userId === aiUserId;
    }

    onUserAvatarError(event: Event): void {
        const target = event.target as HTMLImageElement | null;
        if (!target) {
            return;
        }
        target.src = this.fallbackAvatarUrl;
    }

    onAvatarError(event: Event): void {
        const target = event.target as HTMLImageElement | null;
        if (!target) {
            return;
        }
        target.src = this.fallbackAvatarUrl;
    }

    private findDirectConversation(userId: string): ChatConversationDto | undefined {
        return this.getCanonicalDirectConversation(userId) ?? undefined;
    }

    private getCanonicalDirectConversation(userId: string): ChatConversationDto | null {
        if (!userId) {
            return null;
        }

        const matches = this.conversations.filter((item) =>
            item.type === ChatConversationType.Direct && (item.memberIds ?? []).includes(userId)
        );

        if (matches.length === 0) {
            return null;
        }

        return matches.sort((a, b) => {
            const left = new Date(a.lastMessageAt ?? 0).getTime();
            const right = new Date(b.lastMessageAt ?? 0).getTime();
            return right - left;
        })[0];
    }

    private dedupeDirectConversations(items: ChatConversationDto[]): ChatConversationDto[] {
        const seen = new Set<string>();
        const deduped: ChatConversationDto[] = [];
        const currentUserId = this.currentUser?.id?.toString();

        for (const item of items) {
            if (item.type !== ChatConversationType.Direct) {
                deduped.push(item);
                continue;
            }

            const memberIds = item.memberIds ?? [];
            const otherId = memberIds.find((id) => id !== currentUserId) ?? '';
            const key = otherId ? `direct:${otherId}` : `direct-title:${(item.title ?? '').toLowerCase()}`;
            if (seen.has(key)) {
                continue;
            }

            seen.add(key);
            deduped.push(item);
        }

        return deduped;
    }

    private async loadConversations(): Promise<boolean> {
        try {
            const [conversations, users] = await Promise.all([
                firstValueFrom(this.chatApi.getConversations()),
                firstValueFrom(this.chatApi.getUsersSimple())
            ]);
            this.conversations = conversations ?? [];
            this.users = users ?? [];
            
            // Veritabanından gelen kullanıcılar arasında AI Assistant'ı bul ve eşleştir
            this.syncAiUserFromUsers();
            
            this.conversationRetryAttempts = 0;
            this.syncActiveConversationTitle();
            return true;
        } catch {
            // Keep last known conversations on transient failures
            this.syncActiveConversationTitle();
            return false;
        }
    }

    // Veritabanından gelen kullanıcılar arasında AI Assistant'ı bul
    private syncAiUserFromUsers(): void {
        const aiUserId = this.settingsService.getSettingValue<string>('Chat.AiAssistant.UserId');
        if (!aiUserId) return;
        
        // Users array'inde AI kullanıcısını bul
        const dbAiUser = this.users.find(u => u.id === aiUserId);
        if (dbAiUser) {
            this.aiUser = {
                id: dbAiUser.id,
                name: dbAiUser.name || 'AI Assistant',
                avatarUrl: null as any // CSS gradient avatar kullan
            };
        }
    }

    private scheduleConversationRetry(): void {
        if (this.conversationRetryTimer || this.conversationRetryAttempts >= 3) {
            return;
        }

        if (!this.authService.isUserLoggedIn()) {
            return;
        }

        this.conversationRetryAttempts += 1;
        this.conversationRetryTimer = window.setTimeout(async () => {
            this.conversationRetryTimer = undefined;
            const loaded = await this.loadConversations();
            if (!loaded) {
                this.scheduleConversationRetry();
                return;
            }
            if (!this.activeConversation && this.conversations.length > 0) {
                const next = this.pickInitialConversation(this.conversations);
                if (next) {
                    await this.switchConversation(next.id);
                }
            }
        }, 1200);
    }

    private async handleAuthReady(): Promise<void> {
        const loaded = await this.loadConversations();
        if (!loaded) {
            this.scheduleConversationRetry();
            return;
        }

        if (!this.activeConversation && this.conversations.length > 0) {
            const next = this.pickInitialConversation(this.conversations);
            if (next) {
                await this.switchConversation(next.id);
            }
        }
    }

    private async loadUsers(): Promise<void> {
        this.listLoading = true;
        this.cdr.markForCheck();
        try {
            this.users = await firstValueFrom(this.chatApi.getUsersSimple(this.userQuery || undefined));
            this.cdr.markForCheck();
        } catch {
            this.users = [];
            this.cdr.markForCheck();
        } finally {
            setTimeout(() => {
                this.listLoading = false;
                this.cdr.markForCheck();
            }, 0);
        }
    }

    getConversationTitle(conversation: ChatConversationDto): string {
        if (!conversation) {
            return 'Sohbet';
        }

        if (conversation.title) {
            return conversation.title;
        }

        switch (conversation.type) {
            case ChatConversationType.Ai:
                return 'AI Asistan';
            case ChatConversationType.System:
                return 'Sistem';
            case ChatConversationType.Group:
                return 'Grup Sohbeti';
            default:
                return 'Sohbet';
        }
    }

    private async createAiConversation(): Promise<ChatConversationDto | null> {
        const aiUserId = this.aiUser?.id?.toString();
        const memberIds = this.isGuid(aiUserId) ? [aiUserId] : [];

        const request = {
            type: ChatConversationType.Ai,
            title: this.aiUser?.name ?? 'AI Assistant',
            memberIds
        };

        try {
            return await firstValueFrom(this.chatApi.createConversation(request));
        } catch {
            return null;
        }
    }

    private async loadMessages(conversationId: string): Promise<void> {
        if (!this.messagesLoading) {
            this.messagesLoading = true;
            this.cdr.markForCheck();
        }
        try {
            const page = await firstValueFrom(this.chatApi.getMessages(conversationId, 0, 20));
            const ordered = [...page.items].reverse();
            const mapped = ordered.flatMap((item) => this.mapMessageToUiMessages(item));
            const systemMessages = this.getSystemMessagesForConversation(conversationId);

            if (this.activeConversation?.type === ChatConversationType.Ai) {
                const hasAiReply = ordered.some((item) =>
                    item.type === ChatMessageType.Ai ||
                    item.senderId === this.aiUser?.id?.toString()
                );
                if (hasAiReply) {
                    this.aiSuggestions = [];
                }
            }

            this.messageIds.clear();
            [...systemMessages, ...mapped].forEach((message) => {
                if (message?.id) {
                    this.messageIds.add(message.id.toString());
                }
            });

            this.messages = [...systemMessages, ...mapped];
            this.cdr.markForCheck();
            // DOM'un tamamen güncellenmesini bekle ve en alta scroll
            this.scrollToEndAfterRender();
        } finally {
            // Change detection döngüsünü bozmayacak şekilde bir sonraki tick'te kapat
            setTimeout(() => {
                this.messagesLoading = false;
                this.cdr.markForCheck();
                // Yükleme bitince bir kez daha scroll dene
                this.scrollToBottom(false, true);
            }, 0);
        }
    }

    // DOM güncellendikten sonra en alta scroll
    private scrollToEndAfterRender(): void {
        // Çift requestAnimationFrame: DOM'un tamamen render edilmesini bekle
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                this.scrollToBottom(false, true);
                // Bir kez daha kontrol et
                setTimeout(() => this.scrollToBottom(false, true), 50);
            });
        });
    }

    private handleIncomingMessage(message: ChatMessageDto): void {
        if (!message || !this.activeConversation || message.conversationId !== this.activeConversation.id) {
            return;
        }

        if (this.messageIds.has(message.id)) {
            return;
        }

        const mappedMessages = this.mapMessageToUiMessages(message);
        this.messageIds.add(message.id);
        mappedMessages.forEach((mapped) => {
            if (mapped?.id) {
                this.messageIds.add(mapped.id.toString());
            }
        });
        if (message.type === ChatMessageType.Ai || message.senderId === this.aiUser?.id?.toString()) {
            this.isAiTyping = false;
            this.aiSuggestions = [];
            this.cdr.markForCheck();
        }
        if (message.senderId && message.senderId !== this.currentUser?.id?.toString()) {
            this.clearTypingUser(message.senderId);
        }

        const next = [...this.messages, ...mappedMessages].sort((a, b) => {
            const left = new Date(a.timestamp ?? 0).getTime();
            const right = new Date(b.timestamp ?? 0).getTime();
            return left - right;
        });

        this.messages = next;
        this.cdr.detectChanges();
        this.markConversationRead();
        void this.loadConversations();
        // Kullanıcı yukarıdaysa otomatik scroll yapma (smart scroll)
        this.scrollToBottom(true, false);
    }

    private toDxMessage(message: ChatMessageDto): ChatUiMessage {
        const rawText = message.body ?? '';
        const extracted = this.extractNawizardCard(rawText);
        return {
            id: message.id,
            type: 'text',
            text: extracted.text,
            rawText,
            card: extracted.card,
            timestamp: new Date(message.createdAt),
            author: this.resolveAuthor(message),
            isDeleted: message.isDeleted,
            isEdited: message.isEdited
        } as ChatUiMessage;
    }

    private mapMessageToUiMessages(message: ChatMessageDto): ChatUiMessage[] {
        if (!message) {
            return [];
        }

        const isAi = message.type === ChatMessageType.Ai || message.senderId === this.aiUser?.id?.toString();
        const parts = isAi ? this.tryParseResponseParts(message.body) : null;
        if (!parts || parts.length <= 1) {
            return [this.toDxMessage(message)];
        }

        const baseTime = new Date(message.createdAt).getTime();
        return parts.map((part, index) => {
            const createdAt = new Date(baseTime + index).toISOString();
            const partMessage: ChatMessageDto = {
                ...message,
                id: `${message.id}:${index + 1}`,
                body: part,
                createdAt
            };
            return this.toDxMessage(partMessage);
        });
    }

    private tryParseResponseParts(raw?: string | null): string[] | null {
        const text = (raw ?? '').toString().trim();
        if (!text) {
            return null;
        }

        // JSON array response: ["text", "{card}"]
        if (text.startsWith('[') && text.endsWith(']')) {
            try {
                const parsed = JSON.parse(text);
                if (!Array.isArray(parsed)) {
                    return null;
                }

                const parts: string[] = [];
                for (const item of parsed) {
                    if (typeof item === 'string') {
                        if (item.trim()) {
                            parts.push(item);
                        }
                        continue;
                    }
                    if (item && typeof item === 'object' && typeof (item as any).content === 'string') {
                        const content = (item as any).content.trim();
                        if (content) {
                            parts.push(content);
                        }
                        continue;
                    }
                    if (item && typeof item === 'object' && typeof (item as any).kind === 'string') {
                        const rawItem = JSON.stringify(item);
                        if (rawItem.trim()) {
                            parts.push(rawItem);
                        }
                    }
                }

                return parts.length ? parts : null;
            } catch {
                return null;
            }
        }

        // JSON object response: { response: [...], message: { content }, ... }
        if (text.startsWith('{') && text.endsWith('}')) {
            try {
                const obj = JSON.parse(text);
                if (!obj || typeof obj !== 'object') {
                    return null;
                }

                const parts: string[] = [];
                const response = (obj as any).response;
                if (typeof response === 'string') {
                    if (response.trim()) {
                        parts.push(response.trim());
                    }
                } else if (Array.isArray(response)) {
                    for (const item of response) {
                        if (typeof item === 'string' && item.trim()) {
                            parts.push(item.trim());
                        } else if (item && typeof item === 'object' && typeof item.content === 'string') {
                            const content = item.content.trim();
                            if (content) {
                                parts.push(content);
                            }
                        } else if (item && typeof item === 'object' && typeof item.kind === 'string') {
                            const rawItem = JSON.stringify(item);
                            if (rawItem.trim()) {
                                parts.push(rawItem);
                            }
                        }
                    }
                }

                const message = (obj as any).message;
                if (!parts.length && message && typeof message === 'object' && typeof message.content === 'string') {
                    const content = message.content.trim();
                    if (content) {
                        parts.push(content);
                    }
                }

                return parts.length ? parts : null;
            } catch {
                return null;
            }
        }

        return null;
    }

    private extractNawizardCard(raw: string): { card: NawizardCard | null; text: string } {
        if (!raw) {
            return { card: null, text: '' };
        }

        const trimmed = raw.trim();
        const directEnvelope = this.parseNawizardEnvelope(trimmed);
        if (directEnvelope) {
            return directEnvelope;
        }

        const directCard = this.parseNawizardCardJson(trimmed);
        if (directCard) {
            return { card: directCard, text: '' };
        }

        // If the message has normal text + trailing schema JSON, parse from the last '{'.
        const lastBraceIndex = raw.lastIndexOf('{');
        if (lastBraceIndex >= 0) {
            const tail = raw.slice(lastBraceIndex).trim();
            const tailEnvelope = this.parseNawizardEnvelope(tail);
            if (tailEnvelope?.card) {
                const textPart = raw.slice(0, lastBraceIndex).trim();
                return { card: tailEnvelope.card, text: textPart };
            }
            const tailCard = this.parseNawizardCardJson(tail);
            if (tailCard) {
                const textPart = raw.slice(0, lastBraceIndex).trim();
                return { card: tailCard, text: textPart };
            }
        }

        return { card: null, text: raw };
    }

    private parseNawizardCardJson(raw: string): NawizardCard | null {
        if (!raw) return null;
        if (!raw.startsWith('{') || !raw.endsWith('}')) return null;

        try {
            const obj = JSON.parse(raw);
            if (!obj || typeof obj !== 'object') return null;
            if (typeof obj.kind !== 'string' || typeof obj.version !== 'number') return null;
            if (!obj.data || typeof obj.data !== 'object') return null;

            if (obj.kind === 'nawizard.card.user_profile') {
                if (typeof obj.data.personId !== 'string' || !obj.data.personId) return null;
                return obj as NawizardCard;
            }

            return null;
        } catch {
            return null;
        }
    }

    private parseNawizardEnvelope(raw: string): { card: NawizardCard | null; text: string } | null {
        if (!raw) return null;
        if (!raw.startsWith('{') || !raw.endsWith('}')) return null;

        try {
            const obj = JSON.parse(raw);
            if (!obj || typeof obj !== 'object') return null;
            if (typeof obj.kind !== 'string' || typeof obj.version !== 'number') return null;

            if (obj.kind === 'nawizard.message.text') {
                const text = typeof obj.data?.text === 'string' ? obj.data.text : '';
                return { card: null, text: text ?? '' };
            }

            if (obj.kind === 'nawizard.card.user_profile') {
                if (typeof obj.data?.personId !== 'string' || !obj.data.personId) return null;
                return { card: obj as NawizardCard, text: '' };
            }

            return null;
        } catch {
            return null;
        }
    }

    private resolveAuthor(message: ChatMessageDto): User {
        const senderId = message.senderId;

        if (this.currentUser?.id?.toString() === senderId) {
            return this.currentUser;
        }

        // AI mesaj tipine veya senderId'ye göre kontrol
        if (message.type === ChatMessageType.Ai || this.aiUser?.id?.toString() === senderId) {
            // AI kullanıcısı için sabit avatar (null = CSS gradient avatar)
            return {
                ...this.aiUser,
                id: senderId, // Gerçek senderId'yi kullan
                avatarUrl: null as any
            };
        }

        if (this.systemUser?.id?.toString() === senderId) {
            return this.systemUser;
        }

        const knownUser = this.users.find((user) => user.id === senderId);
        const displayName = message.senderName || knownUser?.name;
        const avatarId = message.senderAvatar || knownUser?.avatar;

        return {
            id: senderId,
            name: displayName ?? (senderId ? `User ${senderId.slice(0, 8)}` : 'User'),
            avatarUrl: this.resolveAvatarUrl(senderId, avatarId, 96)
        };
    }

    private resolveSystemUsers(): void {
        const aiUserId = this.settingsService.getSettingValue<string>('Chat.AiAssistant.UserId');
        const systemUserId = this.settingsService.getSettingValue<string>('General.System.UserId');

        // AI kullanıcısı veritabanından zaten syncAiUserFromUsers ile ayarlanıyor
        // Burada sadece ID'yi kontrol edip users array'inde varsa kullan
        if (this.isGuid(aiUserId) && !this.aiUser?.id) {
            this.aiUser = {
                ...this.aiUser,
                id: aiUserId
            };
        }

        if (this.isGuid(systemUserId)) {
            this.systemUser = {
                ...this.systemUser,
                id: systemUserId
            };
        }
    }

    private getSystemMessagesForConversation(conversationId: string): ChatUiMessage[] {
        const conversation = this.conversations.find((item) => item.id === conversationId);
        const messages: ChatUiMessage[] = [];

        messages.push({
            id: `system-info-${conversationId}`,
            type: 'text',
            text: '🔒 Bu sohbet şifreleme ile korunur ve GDPR uyumludur. Lütfen hassas bilgileri yalnızca gerekli olduğunda paylaşın.',
            rawText: '🔒 Bu sohbet şifreleme ile korunur ve GDPR uyumludur. Lütfen hassas bilgileri yalnızca gerekli olduğunda paylaşın.',
            card: null,
            timestamp: new Date(0),
            author: this.systemUser
        } as ChatUiMessage);

        // AI Asistan artık çalışıyor - hazır değil mesajı kaldırıldı

        return messages;
    }

    private pickInitialConversation(conversations: ChatConversationDto[]): ChatConversationDto | null {
        if (!conversations?.length) {
            return null;
        }

        if (this.conversationId) {
            const requested = conversations.find((item) => item.id === this.conversationId);
            if (requested) {
                return requested;
            }
        }

        const ordered = [...conversations].sort((a, b) => {
            const left = new Date(a.lastMessageAt ?? 0).getTime();
            const right = new Date(b.lastMessageAt ?? 0).getTime();
            return right - left;
        });

        const nonAi = ordered.find((item) => item.type !== ChatConversationType.Ai);
        return nonAi ?? ordered[0] ?? null;
    }

    private isGuid(value: string | null | undefined): boolean {
        if (!value) {
            return false;
        }

        return /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/.test(value);
    }

    private setCurrentUserFromToken(): void {
        const token = this.authService.userAccessToken;
        if (!token) {
            return;
        }

        this.currentUser = {
            id: token.userId ?? 'guest',
            name: token.displayName ?? token.userName ?? 'User',
            avatarUrl: this.resolveAvatarUrl(token.userId ?? null, token.avatar ?? null, 96)
        };
    }

    resolveAvatarUrl(userId?: string | null, avatarId?: string | null, width: number = 96): string {
        if (userId) {
            try {
                return this.imageService.getImageUrl(userId as any, 0.8, userId as any, width);
            } catch {
                // fallback below
            }
        }

        if (avatarId) {
            return this.dataService.getAvatarPhoto(avatarId);
        }

        if (userId) {
            return this.dataService.getAvatarPhoto(userId);
        }

        return this.fallbackAvatarUrl;
    }

    private markConversationRead(conversationId?: string): void {
        const targetId = conversationId ?? this.activeConversation?.id;
        if (!targetId) {
            return;
        }

        this.chatApi.markRead(targetId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => this.chatState.refreshUnreadCount(),
                error: () => { }
            });
    }

    private setActiveConversationAsync(conversation: ChatConversationDto | null): void {
        setTimeout(() => {
            this.activeConversation = conversation;
            this.activeConversationTitle = conversation ? this.getConversationTitle(conversation) : 'Sohbet';
            if (conversation?.type === ChatConversationType.Ai) {
                this.aiSuggestions = [...this.initialAiSuggestions];
            }
            this.updateTypingLabel();
            this.cdr.markForCheck();
        }, 0);
    }

    private syncActiveConversationTitle(): void {
        const conversation = this.activeConversation;
        const next = conversation ? this.getConversationTitle(conversation) : 'Sohbet';
        setTimeout(() => {
            this.activeConversationTitle = next;
            if (conversation?.type === ChatConversationType.Ai) {
                this.aiSuggestions = [...this.initialAiSuggestions];
            }
            this.cdr.markForCheck();
        }, 0);
    }

    toggleEmojiPicker(event?: MouseEvent): void {
        event?.stopPropagation();
        const nextState = !this.showEmojiPicker;
        if (nextState) {
            this.activeEmojiCategory = this.recentEmojis.length > 0 ? 'recent' : 'all';
        }
        this.showEmojiPicker = nextState;
    }

    async sendEmoji(emoji: string, event?: MouseEvent): Promise<void> {
        event?.stopPropagation();
        if (!emoji) {
            return;
        }

        // Insert emoji at cursor position or append to input
        if (this.messageInputEl) {
            const textarea = this.messageInputEl.nativeElement as HTMLTextAreaElement;
            const start = textarea.selectionStart || 0;
            const end = textarea.selectionEnd || 0;
            const before = this.messageInput.substring(0, start);
            const after = this.messageInput.substring(end);
            this.messageInput = before + emoji + after;
            
            // Set cursor position after emoji
            setTimeout(() => {
                textarea.selectionStart = textarea.selectionEnd = start + emoji.length;
                textarea.focus();
            });
        } else {
            this.messageInput += emoji;
        }
        this.onMessageInput();
        this.showEmojiPicker = false;
        this.emojiQuery = '';
        this.trackRecentEmoji(emoji);
    }

    setEmojiCategory(categoryId: string): void {
        this.activeEmojiCategory = categoryId;
    }

    get filteredEmojis(): string[] {
        const query = this.emojiQuery.trim().toLowerCase();
        let list = [...emojiCatalog];

        if (this.activeEmojiCategory === 'recent') {
            const recentList = this.recentEmojis.filter(Boolean);
            if (!query) {
                return recentList;
            }

            return recentList.filter((emoji) => {
                const meta = emojiCatalog.find((item) => normalizeEmoji(item.emoji) === normalizeEmoji(emoji));
                return meta?.keywords?.some((keyword) => keyword.includes(query));
            });
        }

        if (this.activeEmojiCategory !== 'all') {
            list = list.filter((item) => item.category === this.activeEmojiCategory);
        }

        if (query) {
            list = list.filter((item) => item.keywords.some((keyword) => keyword.includes(query)));
        }

        const mapped = list.map((item) => item.emoji);
        
        // Son kullanılan emojileri başa ekle (eşleşme için normalize edilmiş karşılaştırma)
        if (this.activeEmojiCategory === 'all' && !query && this.recentEmojis.length > 0) {
            const recentSet = new Set(this.recentEmojis.map(normalizeEmoji));
            const rest = mapped.filter((emoji) => !recentSet.has(normalizeEmoji(emoji)));
            return [...this.recentEmojis, ...rest];
        }

        return mapped;
    }

    get emojiCategories(): Array<{ id: string; label: string }> {
        const base = [...emojiCategories];

        if (this.recentEmojis.length > 0) {
            return [{ id: 'recent', label: 'Son Kullanılan' }, ...base];
        }

        return base;
    }



    private trackRecentFromText(text: string): void {
        if (!this.isEmojiOnly(text)) {
            return;
        }

        // Emoji segmentasyonu için daha iyi regex
        const emojiRegex = /\p{Extended_Pictographic}(?:\uFE0F?|\u200D\p{Extended_Pictographic})*/gu;
        const matches = text.match(emojiRegex) ?? [];
        
        if (matches.length === 0) {
            return;
        }

        // İlk emojiyi takip et
        this.trackRecentEmoji(matches[0]);
    }

    private loadRecentEmojis(): void {
        try {
            const raw = localStorage.getItem('rtls.chat.recentEmojis');
            const parsed = raw ? JSON.parse(raw) : [];
            if (Array.isArray(parsed)) {
                this.recentEmojis = parsed.filter((item) => typeof item === 'string');
            }
        } catch {
            this.recentEmojis = [];
        }
    }

    private trackRecentEmoji(emoji: string): void {
        const normalizedEmoji = normalizeEmoji(emoji);
        // Mevcut emojileri normalize edip karşılaştır, aynı olanı kaldır
        const filtered = this.recentEmojis.filter((item) => normalizeEmoji(item) !== normalizedEmoji);
        const next = [emoji, ...filtered];
        this.recentEmojis = next.slice(0, 24); // Daha fazla son emoji sakla
        try {
            localStorage.setItem('rtls.chat.recentEmojis', JSON.stringify(this.recentEmojis));
        } catch {
            // ignore storage errors
        }
    }

    private isEmojiOnly(value: string): boolean {
        const trimmed = value.replace(/\s+/g, '');
        if (!trimmed) {
            return false;
        }

        // Daha kapsamlı emoji regex - varyasyon seçicileri ve ZWJ'leri dikkate alır
        const emojiRegex = /\p{Extended_Pictographic}(?:\uFE0F?|\u200D\p{Extended_Pictographic})*/gu;
        const matches = trimmed.match(emojiRegex) ?? [];
        
        // Tüm karakterler emojiye eşleşiyor mu kontrol et
        let matchedLength = 0;
        for (const match of matches) {
            matchedLength += match.length;
        }
        
        return matchedLength === trimmed.length;
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        if (!this.openConversationMenuId) {
            return;
        }

        const target = event.target as HTMLElement | null;
        if (!target) {
            return;
        }

        const insideMenu = target.closest('.chat-popup__list-menu');
        const insideButton = target.closest('.chat-popup__list-menu-button');
        if (insideMenu || insideButton) {
            return;
        }

        this.openConversationMenuId = null;
    }

    // ============================================
    // NEW METHODS FOR CUSTOM CHAT UI
    // ============================================

    async sendMessage(text?: string): Promise<void> {
        const messageText = (text ?? this.messageInput)?.trim();
        if (!messageText || !this.activeConversation?.id) {
            return;
        }

        // Clear input immediately for better UX
        this.messageInput = '';
        this.adjustTextareaHeight();
        this.stopTyping();

        const request: ChatSendMessageRequest = {
            conversationId: this.activeConversation.id,
            body: messageText,
            isImportant: false,
            type: ChatMessageType.Text
        };

        try {
            const message = await firstValueFrom(this.chatApi.sendMessage(request));
            this.handleIncomingMessage(message);
            this.trackRecentFromText(messageText);
            // Kendi mesajımı gönderince mutlaka en alta scroll
            this.scrollToBottom(true, true);
        } catch {
            // Intentionally silent for now; toast can be added later
        }
    }

    async onEnterKey(event: Event): Promise<void> {
        const keyboardEvent = event as KeyboardEvent;
        if (!keyboardEvent.shiftKey) {
            keyboardEvent.preventDefault();
            await this.sendMessage();
            return;
        }
    }

    onMessageInput(): void {
        this.adjustTextareaHeight();
        if (!this.activeConversation?.id || this.activeConversation.type === ChatConversationType.Ai) {
            return;
        }

        if (!this.isTyping) {
            this.isTyping = true;
            this.chatHub.sendTypingStarted(this.activeConversation.id, this.currentUser?.id?.toString() ?? '', this.currentUser?.name ?? '');
        }

        if (this.typingTimeout) {
            clearTimeout(this.typingTimeout);
        }

        this.typingTimeout = window.setTimeout(() => {
            this.stopTyping();
        }, 1800);
    }

    adjustTextareaHeight(): void {
        if (!this.messageInputEl) return;
        
        const textarea = this.messageInputEl.nativeElement as HTMLTextAreaElement;
        textarea.style.height = 'auto';
        textarea.style.height = Math.min(textarea.scrollHeight, 100) + 'px';
    }

    private stopTyping(): void {
        if (!this.isTyping || !this.activeConversation?.id) {
            return;
        }
        this.isTyping = false;
        if (this.typingTimeout) {
            clearTimeout(this.typingTimeout);
            this.typingTimeout = undefined;
        }
        this.chatHub.sendTypingStopped(this.activeConversation.id, this.currentUser?.id?.toString() ?? '');
    }

    private handleTypingStarted(event: { conversationId?: string; userId?: string }): void {
        if (!event?.conversationId || !event.userId || !this.activeConversation?.id) {
            return;
        }

        if (event.conversationId !== this.activeConversation.id || event.userId === this.currentUser?.id?.toString()) {
            return;
        }

        if (event.userId === this.aiUser?.id?.toString()) {
            this.isAiTyping = true;
            this.cdr.markForCheck();
            return;
        }

        this.typingUsers.add(event.userId);
        this.isRemoteTyping = this.typingUsers.size > 0;
        this.updateTypingLabel();

        const existing = this.typingTimers.get(event.userId);
        if (existing) {
            clearTimeout(existing);
        }

        const timer = window.setTimeout(() => {
            this.clearTypingUser(event.userId);
        }, 3500);

        this.typingTimers.set(event.userId, timer);
    }

    private handleTypingStopped(event: { conversationId?: string; userId?: string }): void {
        if (!event?.conversationId || !event.userId || !this.activeConversation?.id) {
            return;
        }

        if (event.conversationId !== this.activeConversation.id) {
            return;
        }

        if (event.userId === this.aiUser?.id?.toString()) {
            this.isAiTyping = false;
            this.cdr.markForCheck();
            return;
        }

        this.clearTypingUser(event.userId);
    }

    private clearTypingUser(userId: string): void {
        this.typingUsers.delete(userId);
        const existing = this.typingTimers.get(userId);
        if (existing) {
            clearTimeout(existing);
            this.typingTimers.delete(userId);
        }
        this.isRemoteTyping = this.typingUsers.size > 0;
        this.updateTypingLabel();
    }

    private clearTypingIndicators(): void {
        this.typingUsers.clear();
        this.typingTimers.forEach((timer) => clearTimeout(timer));
        this.typingTimers.clear();
        this.isRemoteTyping = false;
        this.isAiTyping = false;
        this.updateTypingLabel();
    }

    private updateTypingLabel(): void {
        // iOS tarzı: Sadece nokta animasyonu, yazı yok
        this.cdr.markForCheck();
    }

    scrollToBottom(smooth: boolean = true, force: boolean = false): void {
        if (!this.messagesContainer) return;
        
        const container = this.messagesContainer.nativeElement as HTMLElement;
        
        // Kullanıcı yukarı kaydırmışsa ve force=false ise otomatik scroll yapma
        if (!force) {
            const threshold = 100; // px - en alttan ne kadar uzakta sayılır
            const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < threshold;
            if (!isNearBottom) return;
        }
        
        // Scroll işlemini bir sonraki frame'e ertele (DOM güncellemesi için)
        requestAnimationFrame(() => {
            // Bir kez daha kontrol (DOM değişmiş olabilir)
            const targetScroll = container.scrollHeight;
            if (smooth) {
                container.scrollTo({ top: targetScroll, behavior: 'smooth' });
            } else {
                container.scrollTop = targetScroll;
            }
        });
    }

    copyMessage(text: string): void {
        navigator.clipboard.writeText(text).then(() => {
            this.toastNotify.showGeneralAlert({
                title: 'Kopyalandı',
                content: 'Mesaj panoya kopyalandı.',
                alertType: AlertType.Success
            });
        }).catch(() => {
            // Fallback for older browsers
            const textarea = document.createElement('textarea');
            textarea.value = text;
            textarea.style.position = 'fixed';
            textarea.style.left = '-9999px';
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand('copy');
            document.body.removeChild(textarea);
        });
    }

    async regenerateMessage(message: Message): Promise<void> {
        const lastUserMessage = [...this.messages]
            .reverse()
            .find((item) => item.author?.id === this.currentUser?.id?.toString() && item.text);

        if (!lastUserMessage?.text) {
            return;
        }

        await this.sendMessage(lastUserMessage.text);
    }

    getOtherMemberId(conversation: ChatConversationDto | null): string | null {
        if (!conversation || conversation.type !== ChatConversationType.Direct) {
            return null;
        }

        const currentUserId = this.currentUser?.id?.toString();
        return (conversation.memberIds ?? []).find((id) => id !== currentUserId) ?? null;
    }

    getOtherMemberAvatarUrl(conversation: ChatConversationDto | null): string {
        const otherId = this.getOtherMemberId(conversation);
        if (!otherId) {
            return this.fallbackAvatarUrl;
        }
        const knownUser = this.users.find((user) => user.id === otherId);
        return this.resolveAvatarUrl(otherId, knownUser?.avatar, 96);
    }
}
